import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"
import { mapCoordinateV1Schema } from "./property-mapping-v1.js"

export const dispatchStopDraftV1Schema = z.object({
  stopId: entityIdSchema,
  visitId: entityIdSchema,
  sequence: z.number().int().positive().max(200),
}).strict()

export const dispatchRouteDraftV1Schema = z.object({
  routeId: entityIdSchema,
  assignedWorkerId: entityIdSchema.nullable(),
  assignedCrewId: entityIdSchema.nullable(),
  stops: z.array(dispatchStopDraftV1Schema).min(1).max(200),
}).strict().superRefine((value, context) => {
  if ((value.assignedWorkerId === null) === (value.assignedCrewId === null)) {
    context.addIssue({
      code: "custom",
      path: ["assignedWorkerId"],
      message: "A dispatch route requires exactly one Worker or Crew.",
    })
  }
  const sequences = value.stops.map((stop) => stop.sequence)
  if (
    new Set(sequences).size !== sequences.length
    || [...sequences].sort((left, right) => left - right)
      .some((sequence, index) => sequence !== index + 1)
  ) {
    context.addIssue({
      code: "custom",
      path: ["stops"],
      message: "Stop sequences must be unique and contiguous.",
    })
  }
})

export const dispatchRoutesDraftV1Schema = z.array(dispatchRouteDraftV1Schema)
  .min(1)
  .max(100)
  .superRefine((routes, context) => {
    const routeIds = routes.map((route) => route.routeId)
    const stops = routes.flatMap((route) => route.stops)
    const stopIds = stops.map((stop) => stop.stopId)
    const visitIds = stops.map((stop) => stop.visitId)
    for (const [path, values] of [
      ["routeId", routeIds],
      ["stopId", stopIds],
      ["visitId", visitIds],
    ] as const) {
      if (new Set(values).size !== values.length) {
        context.addIssue({
          code: "custom",
          path: [path],
          message: `Dispatch ${path} values must be unique.`,
        })
      }
    }
    if (stops.length > 200) {
      context.addIssue({
        code: "too_big",
        maximum: 200,
        origin: "array",
        path: ["stops"],
        message: "A Dispatch Run supports at most 200 stops.",
      })
    }
  })

const reason = z.string().trim().min(2).max(500)
const explanationCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)

export const dispatchDraftCreateV1Schema = z.object({
  dispatchRunId: entityIdSchema,
  operatingDate: z.iso.date(),
  routes: dispatchRoutesDraftV1Schema,
  reason,
}).strict()

export const dispatchDraftReviseV1Schema = z.object({
  routes: dispatchRoutesDraftV1Schema,
  reason,
}).strict()

export const dispatchPublishV1Schema = z.object({
  reason,
}).strict()

export const dispatchPlanningConstraintsV1Schema = z.object({
  pinnedStopIds: z.array(entityIdSchema).max(200)
    .refine((values) => new Set(values).size === values.length),
  lockedRouteIds: z.array(entityIdSchema).max(100)
    .refine((values) => new Set(values).size === values.length),
  reason,
}).strict()

const travelMatrix = z.array(
  z.array(z.number().int().nonnegative().max(86_400).nullable()).min(1).max(200),
).min(1).max(200)

export const dispatchRouteProposalRequestV1Schema = z.object({
  proposalId: entityIdSchema,
  providerKey: z.string().regex(/^[a-z][a-z0-9._-]{1,63}$/),
  providerVersion: z.string().trim().min(1).max(100),
  attribution: z.string().trim().min(1).max(500),
  retrievedAt: z.iso.datetime({ offset: true }),
  pointStopIds: z.array(entityIdSchema).min(1).max(200)
    .refine((values) => new Set(values).size === values.length),
  durationsSeconds: travelMatrix,
  distancesMeters: travelMatrix,
  reason,
}).strict().superRefine((value, context) => {
  const size = value.pointStopIds.length
  for (const [path, matrix] of [
    ["durationsSeconds", value.durationsSeconds],
    ["distancesMeters", value.distancesMeters],
  ] as const) {
    if (matrix.length !== size || matrix.some((row) => row.length !== size)) {
      context.addIssue({
        code: "custom",
        path: [path],
        message: "Travel matrices must be square and match pointStopIds.",
      })
    }
  }
})

export const dispatchRouteProposalApplyV1Schema = z.object({
  proposalId: entityIdSchema,
  reason,
}).strict()

export const dispatchVersionReadV1Schema = z.object({
  versionNumber: z.number().int().positive(),
}).strict()

export const dispatchRunResultV1Schema = z.object({
  entityType: z.literal("dispatch_run"),
  entityId: entityIdSchema,
  state: z.enum(["Draft", "Published"]),
  version: z.number().int().positive(),
  draftRevision: z.number().int().positive(),
  latestPublishedVersion: z.number().int().nonnegative(),
  dispatchVersionId: entityIdSchema.nullable(),
  snapshotSha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
}).strict()

export const dispatchPublishedStopV1Schema = dispatchStopDraftV1Schema.extend({
  visitVersion: z.number().int().positive(),
  startsAt: z.iso.datetime({ offset: true }),
  endsAt: z.iso.datetime({ offset: true }),
  timezone: z.string().min(1).max(64),
}).strict()

export const dispatchPublishedRouteV1Schema = z.object({
  routeId: entityIdSchema,
  assignedWorkerId: entityIdSchema.nullable(),
  assignedCrewId: entityIdSchema.nullable(),
  stops: z.array(dispatchPublishedStopV1Schema).min(1).max(200),
}).strict().refine(
  (value) =>
    (value.assignedWorkerId === null) !== (value.assignedCrewId === null),
  {
    path: ["assignedWorkerId"],
    message: "A published route requires exactly one Worker or Crew.",
  },
)

export const dispatchVersionResultV1Schema = z.object({
  entityType: z.literal("dispatch_version"),
  entityId: entityIdSchema,
  state: z.literal("Published"),
  version: z.number().int().positive(),
  dispatchRunId: entityIdSchema,
  operatingDate: z.iso.date(),
  routes: z.array(dispatchPublishedRouteV1Schema).max(100),
  snapshotSha256: z.string().regex(/^[0-9a-f]{64}$/),
}).strict()

const dispatchRouteLegExplanationV1Schema = z.object({
  fromStopId: entityIdSchema,
  toStopId: entityIdSchema,
  durationSeconds: z.number().int().nonnegative(),
  distanceMeters: z.number().int().nonnegative(),
  availableSeconds: z.number().int(),
  feasible: z.boolean(),
}).strict()

const dispatchRouteExplanationV1Schema = z.object({
  routeId: entityIdSchema,
  locked: z.boolean(),
  totalTravelSeconds: z.number().int().nonnegative(),
  totalDistanceMeters: z.number().int().nonnegative(),
  blockerCodes: z.array(explanationCode).max(200),
  legs: z.array(dispatchRouteLegExplanationV1Schema).max(199),
}).strict()

export const dispatchRouteProposalResultV1Schema = z.object({
  entityType: z.literal("route_proposal"),
  entityId: entityIdSchema,
  state: z.enum(["Feasible", "Infeasible"]),
  version: z.literal(1),
  dispatchRunId: entityIdSchema,
  dispatchRunVersion: z.number().int().positive(),
  draftRevision: z.number().int().positive(),
  snapshotSha256: z.string().regex(/^[0-9a-f]{64}$/),
  proposedRoutes: z.array(dispatchPublishedRouteV1Schema).min(1).max(100),
  blockerCodes: z.array(explanationCode).max(200),
  routeExplanations: z.array(dispatchRouteExplanationV1Schema).min(1).max(100),
}).strict()

const boardReason = z.string().trim().min(2).max(500)

export const dispatchBoardStopV1Schema = dispatchPublishedStopV1Schema.extend({
  serviceOrderId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  label: z.string().trim().min(1).max(160),
  address: z.string().trim().min(1).max(500),
  coordinate: mapCoordinateV1Schema.nullable(),
  pinned: z.boolean(),
  blockerCodes: z.array(explanationCode).max(50),
  warningCodes: z.array(explanationCode).max(50).default([]),
}).strict()

export const dispatchBoardRouteV1Schema = z.object({
  routeId: entityIdSchema,
  assignedWorkerId: entityIdSchema.nullable(),
  assignedCrewId: entityIdSchema.nullable(),
  resourceLabel: z.string().trim().min(1).max(160),
  locked: z.boolean(),
  totalTravelSeconds: z.number().int().nonnegative(),
  totalDistanceMeters: z.number().int().nonnegative(),
  scheduledMinutes: z.number().int().nonnegative(),
  blockerCodes: z.array(explanationCode).max(200),
  stops: z.array(dispatchBoardStopV1Schema).min(1).max(200),
}).strict().refine(
  (value) =>
    (value.assignedWorkerId === null) !== (value.assignedCrewId === null),
  {
    path: ["assignedWorkerId"],
    message: "A board route requires exactly one Worker or Crew.",
  },
)

export const dispatchBoardQueueItemV1Schema = z.object({
  visitId: entityIdSchema,
  serviceOrderId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  label: z.string().trim().min(1).max(160),
  address: z.string().trim().min(1).max(500),
  startsAt: z.iso.datetime({ offset: true }),
  endsAt: z.iso.datetime({ offset: true }),
  timezone: z.string().min(1).max(64),
  reasonCodes: z.array(explanationCode).min(1).max(50),
}).strict()

export const dispatchBoardReadV1Schema = z.object({
  responseVersion: z.literal(1),
  dispatchRunId: entityIdSchema,
  legalEntityId: entityIdSchema,
  branchId: entityIdSchema,
  operatingDate: z.iso.date(),
  state: z.enum(["Draft", "Published"]),
  version: z.number().int().positive(),
  draftRevision: z.number().int().positive(),
  latestPublishedVersion: z.number().int().nonnegative(),
  planning: z.object({
    pinnedStopIds: z.array(entityIdSchema).max(200),
    lockedRouteIds: z.array(entityIdSchema).max(100),
    feasibility: z.enum(["Unchecked", "Feasible", "Infeasible"]),
    proposalId: entityIdSchema.nullable(),
  }).strict(),
  permissions: z.object({
    canPlan: z.boolean(),
    canPublish: z.boolean(),
  }).strict(),
  routes: z.array(dispatchBoardRouteV1Schema).max(100),
  unassigned: z.array(dispatchBoardQueueItemV1Schema).max(200),
  exceptions: z.array(dispatchBoardQueueItemV1Schema).max(200),
  proposal: dispatchRouteProposalResultV1Schema.nullable(),
  latestPublication: z.object({
    dispatchVersionId: entityIdSchema,
    versionNumber: z.number().int().positive(),
    snapshotSha256: z.string().regex(/^[0-9a-f]{64}$/),
  }).strict().nullable(),
  diff: z.object({
    affectedRouteCount: z.number().int().nonnegative(),
    movedStopCount: z.number().int().nonnegative(),
  }).strict(),
}).strict()

const dispatchBoardActionBase = {
  dispatchRunId: entityIdSchema,
  expectedVersion: z.number().int().positive(),
  reason: boardReason,
}

export const dispatchBoardActionRequestV1Schema = z.discriminatedUnion("action", [
  z.object({
    ...dispatchBoardActionBase,
    action: z.literal("constraints"),
    pinnedStopIds: z.array(entityIdSchema).max(200)
      .refine((values) => new Set(values).size === values.length),
    lockedRouteIds: z.array(entityIdSchema).max(100)
      .refine((values) => new Set(values).size === values.length),
  }).strict(),
  z.object({
    ...dispatchBoardActionBase,
    action: z.literal("revise"),
    routes: dispatchRoutesDraftV1Schema,
  }).strict(),
  z.object({
    ...dispatchBoardActionBase,
    action: z.literal("apply"),
    proposalId: entityIdSchema,
  }).strict(),
  z.object({
    ...dispatchBoardActionBase,
    action: z.literal("publish"),
  }).strict(),
])

export const dispatchBoardActionResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["confirmed", "conflict", "denied", "retry"]),
  correlationId: z.uuid(),
  errorCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/).nullable(),
  currentVersion: z.number().int().positive().nullable(),
}).strict()

export const dispatchOptimizationStartRequestV1Schema = z.object({
  dispatchRunId: entityIdSchema,
  expectedVersion: z.number().int().positive(),
  reason: boardReason,
}).strict()

export const dispatchOptimizationStartResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  jobId: entityIdSchema,
  status: z.literal("queued"),
}).strict()

export const dispatchOptimizationJobV1Schema = z.object({
  responseVersion: z.literal(1),
  jobId: entityIdSchema,
  status: z.enum(["queued", "running", "complete", "failed", "conflict", "denied"]),
  stage: z.enum(["queued", "travel_inputs", "feasibility", "complete"]),
  progressPercent: z.number().int().min(0).max(100),
  message: z.string().trim().min(1).max(240),
  proposal: dispatchRouteProposalResultV1Schema.nullable(),
  errorCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/).nullable(),
}).strict()

const dispatchDayOfBase = {
  visitId: entityIdSchema,
  expectedVisitVersion: z.number().int().positive(),
}

export const dispatchDayOfChangeV1Schema = z.discriminatedUnion("changeType", [
  z.object({
    ...dispatchDayOfBase,
    changeType: z.literal("reassign"),
    targetRouteId: entityIdSchema,
    assignedWorkerId: entityIdSchema.nullable(),
    assignedCrewId: entityIdSchema.nullable(),
    assignedResourceIds: z.array(entityIdSchema).max(20)
      .refine((values) => new Set(values).size === values.length),
  }).strict().refine(
    (value) =>
      (value.assignedWorkerId === null) !== (value.assignedCrewId === null),
    {
      path: ["assignedWorkerId"],
      message: "A day-of reassignment requires exactly one Worker or Crew.",
    },
  ),
  z.object({
    ...dispatchDayOfBase,
    changeType: z.literal("cancel"),
    cancellationCode: z.enum([
      "customer_cancelled",
      "customer_refused",
      "weather",
      "safety",
      "operational",
    ]),
  }).strict(),
  z.object({
    ...dispatchDayOfBase,
    changeType: z.literal("arrival_window"),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    timezone: z.string().min(1).max(64),
  }).strict().refine(
    (value) => Date.parse(value.startsAt) < Date.parse(value.endsAt),
    { path: ["endsAt"], message: "Arrival window must increase." },
  ),
])

export const dispatchDayOfChangeRequestV1Schema = z.object({
  dispatchRunId: entityIdSchema,
  expectedRunVersion: z.number().int().positive(),
  basePublishedVersion: z.number().int().positive(),
  reason: boardReason,
  change: dispatchDayOfChangeV1Schema,
}).strict()

export const dispatchDayOfChangeResultV1Schema = z.object({
  entityType: z.literal("dispatch_run"),
  entityId: entityIdSchema,
  state: z.literal("Published"),
  version: z.number().int().positive(),
  draftRevision: z.number().int().positive(),
  latestPublishedVersion: z.number().int().positive(),
  dispatchVersionId: entityIdSchema,
  snapshotSha256: z.string().regex(/^[0-9a-f]{64}$/),
  changeId: entityIdSchema,
  changeType: z.enum(["reassign", "cancel", "arrival_window"]),
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  affectedRouteIds: z.array(entityIdSchema).max(2),
  notificationIntentCount: z.number().int().nonnegative().max(10),
}).strict()

export const dispatchDayOfChangeResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["confirmed", "conflict", "denied", "retry"]),
  correlationId: z.uuid(),
  currentRunVersion: z.number().int().positive().nullable(),
  currentPublishedVersion: z.number().int().positive().nullable(),
  result: dispatchDayOfChangeResultV1Schema.nullable(),
  errorCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/).nullable(),
}).strict()

export type DispatchDraftCreateV1 = z.infer<typeof dispatchDraftCreateV1Schema>
export type DispatchDraftReviseV1 = z.infer<typeof dispatchDraftReviseV1Schema>
export type DispatchPublishV1 = z.infer<typeof dispatchPublishV1Schema>
export type DispatchPlanningConstraintsV1 =
  z.infer<typeof dispatchPlanningConstraintsV1Schema>
export type DispatchRouteProposalApplyV1 =
  z.infer<typeof dispatchRouteProposalApplyV1Schema>
export type DispatchRouteProposalRequestV1 =
  z.infer<typeof dispatchRouteProposalRequestV1Schema>
export type DispatchRouteProposalResultV1 =
  z.infer<typeof dispatchRouteProposalResultV1Schema>
export type DispatchBoardActionRequestV1 =
  z.infer<typeof dispatchBoardActionRequestV1Schema>
export type DispatchBoardActionResponseV1 =
  z.infer<typeof dispatchBoardActionResponseV1Schema>
export type DispatchBoardReadV1 = z.infer<typeof dispatchBoardReadV1Schema>
export type DispatchBoardRouteV1 = z.infer<typeof dispatchBoardRouteV1Schema>
export type DispatchBoardStopV1 = z.infer<typeof dispatchBoardStopV1Schema>
export type DispatchOptimizationJobV1 =
  z.infer<typeof dispatchOptimizationJobV1Schema>
export type DispatchOptimizationStartRequestV1 =
  z.infer<typeof dispatchOptimizationStartRequestV1Schema>
export type DispatchOptimizationStartResponseV1 =
  z.infer<typeof dispatchOptimizationStartResponseV1Schema>
export type DispatchDayOfChangeV1 = z.infer<typeof dispatchDayOfChangeV1Schema>
export type DispatchDayOfChangeRequestV1 =
  z.infer<typeof dispatchDayOfChangeRequestV1Schema>
export type DispatchDayOfChangeResponseV1 =
  z.infer<typeof dispatchDayOfChangeResponseV1Schema>
export type DispatchDayOfChangeResultV1 =
  z.infer<typeof dispatchDayOfChangeResultV1Schema>
export type DispatchRouteDraftV1 = z.infer<typeof dispatchRouteDraftV1Schema>
export type DispatchRunResultV1 = z.infer<typeof dispatchRunResultV1Schema>
export type DispatchVersionResultV1 = z.infer<typeof dispatchVersionResultV1Schema>
